import React from 'react';

const TRANSCRIPT_STYLE_ID = 'plynote-transcript-block-style';
if (typeof document !== 'undefined' && !document.getElementById(TRANSCRIPT_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = TRANSCRIPT_STYLE_ID;
  style.textContent = `
    .transcript-block-scroll {
      scrollbar-width: thin;
      scrollbar-color: rgba(148,163,184,0.18) transparent;
    }
    .transcript-block-scroll::-webkit-scrollbar { width: 3px; }
    .transcript-block-scroll::-webkit-scrollbar-track { background: transparent; }
    .transcript-block-scroll::-webkit-scrollbar-thumb {
      background: rgba(148,163,184,0.18);
      border-radius: 999px;
    }
    .transcript-turn {
      transition: opacity 0.3s ease;
    }
    .transcript-bubble {
      transition: background 0.35s ease, border-color 0.35s ease, box-shadow 0.35s ease;
    }
    .transcript-avatar {
      transition: background 0.35s ease, box-shadow 0.35s ease;
    }
    @keyframes transcriptLivePulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.45); }
      50%       { box-shadow: 0 0 0 6px rgba(34,197,94,0); }
    }
    @keyframes transcriptFadeIn {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
}

const hexToRgba = (hex, alpha) => {
  const safe = /^#([A-Fa-f0-9]{6})$/.test(String(hex || '')) ? hex : '#888888';
  const r = parseInt(safe.slice(1, 3), 16);
  const g = parseInt(safe.slice(3, 5), 16);
  const b = parseInt(safe.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const EXPERT_COLOR = '#8B5CF6';

const TranscriptBlock = ({
  audioBlockItem = null,
  currentTimeSeconds = 0,
  isPlaying = false,
  color = '#3B82F6',
  backgroundColor = '#1A1A1A',
  borderColor = '#2A2A2A',
  textColor = '#E0E0E0',
  accentColor = '#9E9E9E',
  maxHeight = 420,
  className = '',
}) => {
  const dialogueTurns = React.useMemo(
    () => (Array.isArray(audioBlockItem?.dialogueTurns) ? audioBlockItem.dialogueTurns : []),
    [audioBlockItem]
  );

  // Find the currently active turn based on playback position
  const currentTurnIndex = React.useMemo(() => {
    if (!dialogueTurns.length) return -1;
    for (let i = dialogueTurns.length - 1; i >= 0; i--) {
      if (currentTimeSeconds >= (dialogueTurns[i].startSeconds ?? 0)) return i;
    }
    return 0;
  }, [currentTimeSeconds, dialogueTurns]);

  const activeTurnRef = React.useRef(null);
  const containerRef = React.useRef(null);

  // Auto-scroll to keep the active turn visible
  React.useEffect(() => {
    if (!isPlaying || currentTurnIndex < 0) return;
    const el = activeTurnRef.current;
    const container = containerRef.current;
    if (!el || !container) return;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const relTop = elRect.top - containerRect.top;
    const relBottom = elRect.bottom - containerRect.top;
    if (relTop < 60 || relBottom > containerRect.height - 60) {
      container.scrollTo({
        top: container.scrollTop + relTop - containerRect.height * 0.38,
        behavior: 'smooth',
      });
    }
  }, [currentTurnIndex, isPlaying]);

  if (!dialogueTurns.length) return null;

  return (
    <div
      className={className}
      style={{
        borderRadius: 20,
        background: backgroundColor,
        border: `1px solid ${hexToRgba(borderColor, 0.6)}`,
        fontFamily: 'Sora, Poppins, Inter, sans-serif',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '14px 20px 10px',
        borderBottom: `1px solid ${hexToRgba(borderColor, 0.4)}`,
      }}>
        <div style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: isPlaying ? '#22c55e' : hexToRgba(accentColor, 0.35),
          flexShrink: 0,
          transition: 'background 0.4s ease',
          animation: isPlaying ? 'transcriptLivePulse 1.8s ease infinite' : 'none',
        }} />
        <span style={{
          fontSize: '0.62rem',
          fontWeight: 700,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: hexToRgba(accentColor, 0.6),
        }}>
          Live transcript
        </span>

        {/* Speaker legend */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 14, alignItems: 'center' }}>
          {[
            { key: 'HOST', label: 'Host', c: color },
            { key: 'EXPERT', label: 'Expert', c: EXPERT_COLOR },
          ].map(({ key, label, c }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: c }} />
              <span style={{ fontSize: '0.62rem', fontWeight: 600, color: hexToRgba(accentColor, 0.6), letterSpacing: '0.06em' }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Turns */}
      <div
        ref={containerRef}
        className="transcript-block-scroll"
        style={{ maxHeight, overflowY: 'auto', padding: '18px 20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        {dialogueTurns.map((turn, i) => {
          const isHost = turn.speakerKey === 'HOST';
          const isActive = i === currentTurnIndex;
          const speakerColor = isHost ? color : EXPERT_COLOR;

          return (
            <div
              key={i}
              ref={isActive ? activeTurnRef : null}
              className="transcript-turn"
              style={{
                display: 'flex',
                flexDirection: isHost ? 'row' : 'row-reverse',
                alignItems: 'flex-start',
                gap: 10,
                opacity: isActive ? 1 : 0.55,
                animation: i === 0 ? `transcriptFadeIn 0.4s ease both` : undefined,
              }}
            >
              {/* Avatar */}
              <div
                className="transcript-avatar"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  background: isActive ? speakerColor : hexToRgba(speakerColor, 0.22),
                  border: `1.5px solid ${isActive ? speakerColor : hexToRgba(speakerColor, 0.35)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  color: isActive ? '#fff' : hexToRgba(speakerColor, 0.9),
                  flexShrink: 0,
                  boxShadow: isActive ? `0 0 0 3px ${hexToRgba(speakerColor, 0.18)}` : 'none',
                  letterSpacing: 0,
                }}
              >
                {turn.speaker?.[0] ?? (isHost ? 'H' : 'E')}
              </div>

              {/* Bubble */}
              <div
                className="transcript-bubble"
                style={{
                  maxWidth: '74%',
                  background: isActive
                    ? hexToRgba(speakerColor, 0.1)
                    : hexToRgba('#000000', 0.12),
                  borderRadius: isHost
                    ? '4px 16px 16px 16px'
                    : '16px 4px 16px 16px',
                  padding: '10px 14px 11px',
                  border: `1px solid ${isActive ? hexToRgba(speakerColor, 0.32) : hexToRgba('#ffffff', 0.05)}`,
                  boxShadow: isActive ? `0 2px 18px ${hexToRgba(speakerColor, 0.12)}` : 'none',
                }}
              >
                <div style={{
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: speakerColor,
                  marginBottom: 5,
                }}>
                  {turn.speaker ?? (isHost ? 'Host' : 'Expert')}
                </div>
                <div style={{
                  fontSize: '0.875rem',
                  lineHeight: 1.65,
                  color: hexToRgba(textColor, isActive ? 0.92 : 0.62),
                  letterSpacing: '0.01em',
                }}>
                  {turn.text}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom fade */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 40,
          background: `linear-gradient(to top, ${backgroundColor} 0%, transparent 100%)`,
          pointerEvents: 'none',
        }}
      />
    </div>
  );
};

export default TranscriptBlock;
